/**
 * Partial PII masking for candidate cross-check on login.
 * Enough to recognise "this is me", not enough to scrape full values.
 */

const BULLET = "•";

function bullets(n) {
  return BULLET.repeat(Math.max(1, n));
}

/** Mask a free-text value (branch, school, program, etc.). */
export function maskText(value, { keepStart = 3, keepEnd = 2 } = {}) {
  const s = String(value || "").trim();
  if (!s) return "";
  if (s.length <= 3) return `${s[0]}${bullets(2)}`;
  if (s.length <= keepStart + keepEnd) {
    return `${s.slice(0, 1)}${bullets(s.length - 1)}`;
  }
  const start = s.slice(0, keepStart);
  const end = s.slice(-keepEnd);
  const mid = Math.min(6, s.length - keepStart - keepEnd);
  return `${start}${bullets(mid)}${end}`;
}

/** Mask each word in a name: "Visvesh Naraharisetty" → "Vi••••• Na••••••••••" */
export function maskName(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  return s
    .split(/\s+/)
    .map((word) => {
      if (word.length <= 1) return word;
      if (word.length === 2) return `${word[0]}${BULLET}`;
      const keep = word.length <= 4 ? 1 : 2;
      return `${word.slice(0, keep)}${bullets(Math.min(6, word.length - keep))}`;
    })
    .join(" ");
}

/** Mask email local-part; keep a hint of the domain (preserve edu.in / co.uk style TLDs). */
export function maskEmail(value) {
  const s = String(value || "").trim();
  if (!s || !s.includes("@")) return maskText(s);
  const [local, domain] = s.split("@");
  const localMasked =
    local.length <= 2
      ? `${local[0] || BULLET}${bullets(2)}`
      : `${local.slice(0, 2)}${bullets(Math.min(5, local.length - 2))}`;
  const parts = domain.split(".");
  const keepTail = parts.length >= 3 ? 2 : 1;
  const maskedDomain = parts
    .map((part, i) => {
      if (i >= parts.length - keepTail) return part;
      if (part.length <= 2) return `${part[0] || BULLET}${BULLET}`;
      return `${part.slice(0, 2)}${bullets(Math.min(5, part.length - 2))}`;
    })
    .join(".");
  return `${localMasked}@${maskedDomain}`;
}

/** Mask phone — show last 2–4 digits only. */
export function maskPhone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length <= 4) return bullets(digits.length);
  const keep = Math.min(4, Math.max(2, digits.length - 6));
  return `${bullets(Math.min(6, digits.length - keep))}${digits.slice(-keep)}`;
}
